import React, { useCallback, useState, FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import _ from 'lodash';
// mui components
import { Box, Button, Switch, Grid, Typography } from '@mui/material';
// components
import User from 'components/file-cabinet-folders/modals/components/user';
import ActionButton from 'components/buttons/index';
import CompanyRolesTable from '../company-roles-table';
// hooks
import useToast from 'hooks/useToast';
import { getEnvironmentConfig } from 'utils/environment';
import { isCustomProjectFolder } from 'utils/helpers/fileCabinetHelpers';
// api
import {
  IFolder,
  TFileCabinetRole,
  useUpdateFileCabinetUserMutation,
  useUpdateFoldersVisibilityMutation,
} from 'redux-store/api/file-cabinet';
import { useGetCompanyRolesQuery } from 'redux-store/api/company-roles';
// icons
import CopyIcon from 'assets/icons/cabinet/copy.svg';
// styles
import { styles } from './styles';

type Props = {
  folder?: IFolder;
  projectId: string | number;
  handleClose: () => void;
};

type PermissionsFormValues = {
  users: IFolder['users'];
  roles: TFileCabinetRole[];
};

const buildRolesFormValues = (
  companyRoles: { id: number; name: string }[],
  folder?: IFolder,
): TFileCabinetRole[] =>
  companyRoles.map(({ id, name }) => {
    const savedRole = folder?.roles?.find((role) => Number(role.id) === Number(id));

    return {
      id,
      name,
      permissions: savedRole?.permissions ?? {
        canRead: false,
        canAddUpdate: false,
        canDelete: false,
      },
    };
  });

const ManagePermissionsUsersContent: FC<Props> = ({ folder, projectId, handleClose }) => {
  const [isPublic, setIsPublic] = useState(false);
  const showToast = useToast();

  const { data: companyRoles = [] } = useGetCompanyRolesQuery();
  const [updateCabinetUsers, { isLoading }] = useUpdateFileCabinetUserMutation();
  const [updateFoldersVisibility, { isLoading: isLoadingFolderVisibility }] =
    useUpdateFoldersVisibilityMutation();

  useEffect(() => {
    if (!!folder?.isPublic) {
      setIsPublic(folder?.isPublic);
    }
  }, [folder]);

  const getInitials = (name: string) => {
    const [firstName, lastName] = name.split(' ');
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
  };

  const permissionsForm = useForm<PermissionsFormValues>({
    defaultValues: {
      users: folder?.users ? folder?.users : [],
      roles: [],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isCustomProjectFolder(folder) || !companyRoles.length) {
      return;
    }

    permissionsForm.setValue('roles', buildRolesFormValues(companyRoles, folder));
  }, [companyRoles, folder, permissionsForm]);

  const fields = permissionsForm.watch('users');

  const onUsersSubmit = useCallback(
    async (formValues: PermissionsFormValues) => {
      const editedObjects = formValues?.users?.filter((editedItem) => {
        const originalItem = folder?.users?.find(
          (item) => Number(item?.id) === Number(editedItem?.id),
        );

        return !_.isEqual(originalItem, editedItem);
      });

      const updatingUsersData =
        editedObjects?.map((item) => ({
          profileId: Number(item?.id),
          itemId: Number(folder?.id),
          permissions: item?.permissions,
        })) ?? [];

      const editedRoles = isCustomProjectFolder(folder)
        ? formValues.roles.filter((editedRole) => {
            const savedRole = folder?.roles?.find(
              (role) => Number(role.id) === Number(editedRole.id),
            );
            const originalRole = {
              id: editedRole.id,
              name: editedRole.name,
              permissions: savedRole?.permissions ?? {
                canRead: false,
                canAddUpdate: false,
                canDelete: false,
              },
            };

            return !_.isEqual(originalRole, editedRole);
          })
        : [];

      if (!updatingUsersData.length && !editedRoles.length) {
        return;
      }

      updateCabinetUsers({
        items: updatingUsersData,
        itemId: Number(folder?.id),
        ...(editedRoles.length ? { roles: editedRoles } : {}),
      })
        .unwrap()
        .then(() => {
          showToast({
            message: 'Updated successfully!',
            type: 'success',
          });
        })
        .catch(() => {
          showToast({
            message: 'Something went wrong!',
            type: 'error',
          });
        });
    },
    [folder, updateCabinetUsers, showToast],
  );

  const toggleMakePublic = useCallback((e) => {
    updateFoldersVisibility({ itemId: folder.id, isPublic: e.target.checked, projectId: projectId })
      .unwrap()
      .then(() => {
        showToast({ type: 'success', message: 'Updated successfully' });
      })
      .catch(() => {
        showToast({ type: 'error', message: 'Something went wrong' });
      });

    setIsPublic((prev) => !prev);
  }, []);

  const handleCopy = async () => {
    try {
      if (!!folder?.accessKey) {
        const { publicUrl } = getEnvironmentConfig();
        await navigator.clipboard.writeText(
          `${publicUrl}public-file-cabinet?accessKey=${folder?.accessKey}`,
        );
        showToast({ type: 'success', message: 'Link copied' });
      }
    } catch (err) {
      showToast({ type: 'error', message: 'Something went wrong' });
    }
  };

  return (
    <Box>
      <FormProvider {...permissionsForm}>
        <form onSubmit={permissionsForm.handleSubmit(onUsersSubmit)}>
          <Box sx={styles.usersContainer}>
            <Grid sx={styles.rolesTableHead}>
              <Grid sx={styles.roleTableTitle}>User</Grid>
              <Grid sx={styles.roleTableItem}>View</Grid>
              <Grid sx={styles.roleTableItem}>Add/Edit</Grid>
              <Grid sx={styles.roleTableItem}>Delete</Grid>
              <Grid sx={styles.roleTableEmptyItem}></Grid>
            </Grid>

            {fields?.map((user, index) => (
              <User
                key={`${user?.permissions?.canRead}+${user?.permissions?.canAddUpdate}+${user?.permissions?.canDelete}+${index}`}
                user={user}
                index={index}
                getInitials={getInitials}
              />
            ))}
          </Box>

          <CompanyRolesTable folder={folder} />

          <Grid sx={styles.makePublicWrapper}>
            <Grid>
              <Typography sx={styles.makePublicTitle}>Make public</Typography>
              <Typography sx={styles.makePublicSubTitle}>
                Copy link and send to your friend.
              </Typography>
            </Grid>

            <Switch
              sx={styles.switch}
              onChange={toggleMakePublic}
              disabled={isLoadingFolderVisibility}
              defaultChecked={folder?.isPublic}
            />

            {!!isPublic && (
              <Grid sx={styles.copyBtnWrapper}>
                <Typography sx={styles.copyUrl}>
                  {folder?.accessKey
                    ? `${getEnvironmentConfig().publicUrl}public-file-cabinet?accessKey=${folder?.accessKey}`
                    : ''}
                </Typography>

                <Button
                  startIcon={<CopyIcon />}
                  sx={styles.copyBtn}
                  disableRipple
                  onClick={handleCopy}
                  disabled={!folder?.accessKey}
                >
                  Copy
                </Button>
              </Grid>
            )}
          </Grid>

          <Grid sx={styles.actions}>
            <ActionButton variant="secondary" onClick={handleClose} label="Cancel" />
            <ActionButton loading={isLoading} type="submit" label="Save" />
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
};

export default ManagePermissionsUsersContent;
